import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronRight, Image as ImageIcon, CheckCircle2, Trash2, X, Loader2, Inbox, ZoomIn, ZoomOut, RotateCcw, Calendar, CalendarDays
} from 'lucide-react';
import { remittancesService } from '../../services';
import './RemittanceManagement.css';

const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(value);
  } catch {
    return `${currency || ''} ${Number(value).toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Group by the date the check was UPLOADED/received (created_at), not the
// date printed on the check itself (check_date) - the whole point of this
// view is "which batch did I upload and when", so a check dated weeks
// earlier than when it was actually sent to the bot should still land under
// the day it was uploaded, not scatter across whatever dates are printed on
// the documents.
const getGroupingDate = (check) => check.created_at;

const monthKeyOf = (dateStr) => dateStr ? dateStr.slice(0, 7) : 'unknown'; // YYYY-MM
const dayKeyOf = (dateStr) => dateStr ? dateStr.slice(0, 10) : 'unknown'; // YYYY-MM-DD

const monthLabelOf = (monthKey) => {
  if (monthKey === 'unknown') return 'Unknown Date';
  const [y, m] = monthKey.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

const dayLabelOf = (dayKey) => {
  if (dayKey === 'unknown') return 'Unknown Date';
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const RemittanceManagement = () => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [imageModal, setImageModal] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState(new Set());

  const loadChecks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.supplier = search.trim();
      const data = await remittancesService.getAll(params);
      setChecks(data.checks || []);
    } catch (err) {
      console.error('Failed to load remittances:', err);
      setError(err.response?.data?.error || 'Failed to load remittances');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const timeoutId = setTimeout(loadChecks, 300);
    return () => clearTimeout(timeoutId);
  }, [loadChecks]);

  // Build the Month > Day > Checks hierarchy. Most recent month/day first.
  const monthGroups = useMemo(() => {
    const months = new Map();

    for (const check of checks) {
      const groupingDate = getGroupingDate(check);
      const mKey = monthKeyOf(groupingDate);
      const dKey = dayKeyOf(groupingDate);

      if (!months.has(mKey)) {
        months.set(mKey, { monthKey: mKey, days: new Map(), count: 0, totalAmount: 0 });
      }
      const month = months.get(mKey);
      if (!month.days.has(dKey)) {
        month.days.set(dKey, { dayKey: dKey, checks: [], count: 0, totalAmount: 0 });
      }
      const day = month.days.get(dKey);
      day.checks.push(check);
      day.count += 1;
      day.totalAmount += Number(check.amount) || 0;
      month.count += 1;
      month.totalAmount += Number(check.amount) || 0;
    }

    return Array.from(months.values())
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
      .map(month => ({
        ...month,
        days: Array.from(month.days.values()).sort((a, b) => b.dayKey.localeCompare(a.dayKey))
      }));
  }, [checks]);

  // Auto-expand the most recent month/day so the page isn't empty-looking on load.
  useEffect(() => {
    if (monthGroups.length > 0 && expandedMonths.size === 0 && expandedDays.size === 0) {
      setExpandedMonths(new Set([monthGroups[0].monthKey]));
      if (monthGroups[0].days.length > 0) {
        setExpandedDays(new Set([`${monthGroups[0].monthKey}|${monthGroups[0].days[0].dayKey}`]));
      }
    }
  }, [monthGroups.length]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(monthKey) ? next.delete(monthKey) : next.add(monthKey);
      return next;
    });
  };

  const toggleDay = (dayId) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      next.has(dayId) ? next.delete(dayId) : next.add(dayId);
      return next;
    });
  };

  const toggleExpand = async (checkId) => {
    if (expandedId === checkId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(checkId);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const data = await remittancesService.getById(checkId);
      setExpandedDetail(data);
    } catch (err) {
      console.error('Failed to load remittance detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMarkReviewed = async (checkId) => {
    try {
      await remittancesService.markReviewed(checkId);
      setChecks(prev => prev.map(c => c.id === checkId ? { ...c, status: 'reviewed' } : c));
    } catch (err) {
      console.error('Failed to mark reviewed:', err);
    }
  };

  const handleDelete = async (checkId) => {
    if (!window.confirm('Delete this remittance record? This cannot be undone.')) return;
    try {
      await remittancesService.remove(checkId);
      setChecks(prev => prev.filter(c => c.id !== checkId));
      if (expandedId === checkId) {
        setExpandedId(null);
        setExpandedDetail(null);
      }
    } catch (err) {
      console.error('Failed to delete remittance:', err);
    }
  };

  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.5;

  const openImageModal = async (checkId, imagePaths) => {
    setImageModal({ checkId, count: imagePaths.length, activeIndex: 0, urls: {}, loading: true, zoom: 1 });
    try {
      const url = await remittancesService.getImageBlobUrl(checkId, 0);
      setImageModal(prev => prev && prev.checkId === checkId
        ? { ...prev, urls: { ...prev.urls, 0: url }, loading: false }
        : prev);
    } catch (err) {
      console.error('Failed to load remittance image:', err);
      setImageModal(prev => prev && prev.checkId === checkId ? { ...prev, loading: false, error: true } : prev);
    }
  };

  const loadImageTab = async (index) => {
    if (!imageModal || imageModal.urls[index]) {
      setImageModal(prev => prev ? { ...prev, activeIndex: index, zoom: 1 } : prev);
      return;
    }
    setImageModal(prev => ({ ...prev, activeIndex: index, loading: true, zoom: 1 }));
    try {
      const url = await remittancesService.getImageBlobUrl(imageModal.checkId, index);
      setImageModal(prev => prev ? { ...prev, urls: { ...prev.urls, [index]: url }, loading: false } : prev);
    } catch (err) {
      console.error('Failed to load remittance image:', err);
      setImageModal(prev => prev ? { ...prev, loading: false, error: true } : prev);
    }
  };

  const zoomIn = () => setImageModal(prev => prev ? { ...prev, zoom: Math.min(ZOOM_MAX, (prev.zoom || 1) + ZOOM_STEP) } : prev);
  const zoomOut = () => setImageModal(prev => prev ? { ...prev, zoom: Math.max(ZOOM_MIN, (prev.zoom || 1) - ZOOM_STEP) } : prev);
  const zoomReset = () => setImageModal(prev => prev ? { ...prev, zoom: 1 } : prev);

  const closeImageModal = () => {
    if (imageModal) {
      Object.values(imageModal.urls).forEach(url => URL.revokeObjectURL(url));
    }
    setImageModal(null);
  };

  const renderChecksTable = (dayChecks) => (
    <div className="remittance-table-scroll">
      <table className="remittance-table">
        <thead>
          <tr>
            <th style={{ width: 32 }}></th>
            <th>Supplier</th>
            <th>Check Date</th>
            <th>Check #</th>
            <th>Amount</th>
            <th>Agent</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {dayChecks.map(check => (
            <React.Fragment key={check.id}>
              <tr
                className={`remittance-row${expandedId === check.id ? ' expanded' : ''}`}
                onClick={() => toggleExpand(check.id)}
              >
                <td>
                  <button className={`remittance-expand-toggle${expandedId === check.id ? ' open' : ''}`}>
                    <ChevronRight size={18} />
                  </button>
                </td>
                <td>{check.supplier_name}</td>
                <td>{formatDate(check.check_date)}</td>
                <td>{check.check_number || '—'}</td>
                <td>{formatCurrency(check.amount, check.currency)}</td>
                <td>{check.agent_name || '—'}</td>
                <td>
                  <span className={`remittance-badge ${check.status}`}>
                    {check.status === 'reviewed' ? 'Reviewed' : 'Needs Review'}
                  </span>
                </td>
              </tr>
              {expandedId === check.id && (
                <tr className="remittance-detail-row">
                  <td colSpan={7}>
                    <div className="remittance-detail-panel">
                      {detailLoading || !expandedDetail ? (
                        <Loader2 className="remittance-spin" size={20} />
                      ) : (
                        <>
                          <div className="remittance-detail-summary">
                            <div className="stat">
                              <label>Total Commission Due</label>
                              <span>{formatCurrency(expandedDetail.check.total_commission_due, expandedDetail.check.currency)}</span>
                            </div>
                            <div className="stat">
                              <label>Payment Total</label>
                              <span>{formatCurrency(expandedDetail.check.payment_total, expandedDetail.check.currency)}</span>
                            </div>
                            <div className="stat">
                              <label>Line Items</label>
                              <span>{expandedDetail.lineItems.length}</span>
                            </div>
                            <div className="stat">
                              <label>Received</label>
                              <span>{formatDate(expandedDetail.check.created_at)}</span>
                            </div>
                          </div>

                          <div className="remittance-detail-actions">
                            {expandedDetail.check.image_paths?.length > 0 && (
                              <button
                                className="remittance-btn primary"
                                onClick={() => openImageModal(check.id, expandedDetail.check.image_paths)}
                              >
                                <ImageIcon size={15} /> View Photo{expandedDetail.check.image_paths.length > 1 ? 's' : ''}
                              </button>
                            )}
                            {check.status !== 'reviewed' && (
                              <button className="remittance-btn" onClick={() => handleMarkReviewed(check.id)}>
                                <CheckCircle2 size={15} /> Mark Reviewed
                              </button>
                            )}
                            <button className="remittance-btn danger" onClick={() => handleDelete(check.id)}>
                              <Trash2 size={15} /> Delete
                            </button>
                          </div>

                          {expandedDetail.lineItems.length > 0 && (
                            <div className="remittance-table-scroll">
                              <table className="remittance-line-items-table">
                                <thead>
                                  <tr>
                                    <th>Hotel</th>
                                    <th>Guest</th>
                                    <th>Conf #</th>
                                    <th>Arrival</th>
                                    <th>Departure</th>
                                    <th>Rooms</th>
                                    <th>Status</th>
                                    <th>Revenue</th>
                                    <th>Gross Comm</th>
                                    <th>Adjusted</th>
                                    <th>Tax Paid</th>
                                    <th>Comm Due</th>
                                    <th>Currency</th>
                                    <th>Comm Paid</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedDetail.lineItems.map(item => (
                                    <tr key={item.id}>
                                      <td>{item.hotel_name || '—'}</td>
                                      <td>{item.guest_name || '—'}</td>
                                      <td>{item.confirmation_number || '—'}</td>
                                      <td>{formatDate(item.arrival_date)}</td>
                                      <td>{formatDate(item.departure_date)}</td>
                                      <td>{item.num_rooms ?? '—'}</td>
                                      <td>{item.status || '—'}</td>
                                      <td>{formatCurrency(item.revenue, item.currency)}</td>
                                      <td>{formatCurrency(item.gross_commission, item.currency)}</td>
                                      <td>{formatCurrency(item.adjusted_amount, item.currency)}</td>
                                      <td>{formatCurrency(item.tax_paid, item.currency)}</td>
                                      <td>{formatCurrency(item.commission_due, item.currency)}</td>
                                      <td>{item.currency || '—'}</td>
                                      <td>{formatCurrency(item.commission_paid, item.currency)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {expandedDetail.check.ai_warnings && (
                            <div className="remittance-warnings">
                              ⚠️ {expandedDetail.check.ai_warnings}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="remittance-page">
      <div className="remittance-header">
        <div>
          <h1>Remittances</h1>
          <p>Supplier commission checks and remittance advices received via the Telegram intake bot.</p>
        </div>
      </div>

      <div className="remittance-filters">
        <input
          type="search"
          placeholder="Search by supplier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="needs_review">Needs Review</option>
          <option value="reviewed">Reviewed</option>
        </select>
      </div>

      <div className="remittance-groups">
        {loading ? (
          <div className="remittance-empty"><Loader2 className="remittance-spin" size={28} /></div>
        ) : error ? (
          <div className="remittance-empty">{error}</div>
        ) : monthGroups.length === 0 ? (
          <div className="remittance-empty">
            <Inbox size={32} style={{ marginBottom: 8 }} />
            <div>No remittances yet. Send a photo of a check to the Telegram remittance bot to get started.</div>
          </div>
        ) : (
          monthGroups.map(month => {
            const monthOpen = expandedMonths.has(month.monthKey);
            return (
              <div className="remittance-month-group" key={month.monthKey}>
                <button className="remittance-month-header" onClick={() => toggleMonth(month.monthKey)}>
                  <ChevronRight size={18} className={monthOpen ? 'remittance-chevron open' : 'remittance-chevron'} />
                  <Calendar size={16} />
                  <span className="remittance-month-label">{monthLabelOf(month.monthKey)}</span>
                  <span className="remittance-month-meta">{month.count} check{month.count !== 1 ? 's' : ''} · {formatCurrency(month.totalAmount)}</span>
                </button>

                {monthOpen && (
                  <div className="remittance-day-list">
                    {month.days.map(day => {
                      const dayId = `${month.monthKey}|${day.dayKey}`;
                      const dayOpen = expandedDays.has(dayId);
                      return (
                        <div className="remittance-day-group" key={dayId}>
                          <button className="remittance-day-header" onClick={() => toggleDay(dayId)}>
                            <ChevronRight size={16} className={dayOpen ? 'remittance-chevron open' : 'remittance-chevron'} />
                            <CalendarDays size={14} />
                            <span className="remittance-day-label">{dayLabelOf(day.dayKey)}</span>
                            <span className="remittance-day-meta">{day.count} check{day.count !== 1 ? 's' : ''} · {formatCurrency(day.totalAmount)}</span>
                          </button>

                          {dayOpen && (
                            <div className="remittance-day-body">
                              {renderChecksTable(day.checks)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {imageModal && (
        <div className="remittance-image-modal-backdrop" onClick={closeImageModal}>
          <div className="remittance-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="remittance-image-modal-header">
              <strong>Check / Remittance Photo</strong>
              <div className="remittance-image-zoom-controls">
                <button onClick={zoomOut} disabled={imageModal.zoom <= ZOOM_MIN} title="Zoom out"><ZoomOut size={18} /></button>
                <span>{Math.round((imageModal.zoom || 1) * 100)}%</span>
                <button onClick={zoomIn} disabled={imageModal.zoom >= ZOOM_MAX} title="Zoom in"><ZoomIn size={18} /></button>
                <button onClick={zoomReset} disabled={imageModal.zoom === 1} title="Reset zoom"><RotateCcw size={16} /></button>
                <button onClick={closeImageModal} title="Close"><X size={20} /></button>
              </div>
            </div>
            {imageModal.count > 1 && (
              <div className="remittance-image-tabs">
                {Array.from({ length: imageModal.count }).map((_, i) => (
                  <button
                    key={i}
                    className={imageModal.activeIndex === i ? 'active' : ''}
                    onClick={() => loadImageTab(i)}
                  >
                    Page {i + 1}
                  </button>
                ))}
              </div>
            )}
            <div className="remittance-image-modal-body">
              {imageModal.loading ? (
                <Loader2 className="remittance-spin" size={28} />
              ) : imageModal.error ? (
                <div>Failed to load image.</div>
              ) : (
                <img
                  src={imageModal.urls[imageModal.activeIndex]}
                  alt="Check/remittance"
                  style={(imageModal.zoom || 1) > 1 ? {
                    // Scale via width% (not CSS transform) so the image actually
                    // grows past the container's box and creates real scrollable
                    // overflow (transform: scale() doesn't affect layout/reflow,
                    // so the modal body's overflow:auto would never kick in).
                    width: `${imageModal.zoom * 100}%`,
                    maxWidth: 'none',
                    maxHeight: 'none',
                    cursor: 'zoom-out'
                  } : { cursor: 'zoom-in' }}
                  onClick={() => (imageModal.zoom || 1) > 1 ? zoomReset() : zoomIn()}
                  onWheel={(e) => {
                    e.preventDefault();
                    if (e.deltaY < 0) zoomIn(); else zoomOut();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemittanceManagement;
